import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from './Sidebar.module.css';
import logo from '../../assets/images/logo2.png';

interface SidebarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
  activeItem?: string;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href: string;
  badge?: number;
  subItems?: SubNavItem[];
}

interface SubNavItem {
  id: string;
  label: string;
  href: string;
}

const IDefineSidebar: React.FC<SidebarProps> = ({ 
  isCollapsed = false, 
  onToggle,
  activeItem = 'dashboard'
}) => {
  const location = useLocation();
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const navigate = useNavigate();

  const navigationItems: NavItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      href: '/dashboard',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
          <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
          <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
          <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
    },
    {
      id: 'assigned-studies',
      label: 'Assigned Studies',
      href: '/assigned-studies',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M9 12h6M9 16h6M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9l-7-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M13 2v7h7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
    },
    {
      id: 'create-study-configuration',
      label: 'Create Study',
      href: '/create-study-configuration',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
    },
    {
      id: 'define-xml',
      label: 'Define.XML',
      href: '/define-xml',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="2"/>
          <path d="M14 2v6h6M12 18v-6M9 15l3-3 3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
    },
    {
      id: 'issue-management-1',
      label: 'Issue Management',
      href: '/issue-management',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
    },
    {
      id: 'reviewers-guide',
      label: "Reviewer's Guide",
      href: '/reviewers-guide',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M4 19.5A2.5 2.5 0 016.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
    },
    {
      id: 'annotated-crf',
      label: 'Annotated CRF',
      href: '/annotated-crf',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="2"/>
          <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
    },
    {
      id: 'divider-1',
      label: '',
      href: '',
      icon: null,
    },
    {
      id: 'dataset-validation',
      label: 'Dataset Validation',
      href: '/dataset-validation',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M9 11l3 3L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
    },
    {
      id: 'issue-management-2',
      label: 'Issue Management',
      href: '/issue-management-2',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 9v4M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
    },
    {
      id: 'divider-2',
      label: '',
      href: '',
      icon: null,
    },
    {
      id: 'reports',
      label: 'Reports',
      href: '/reports',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M3 3v18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M18 17V9M13 17V5M8 17v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
    },
    {
      id: 'administration',
      label: 'Administration',
      href: '/administration',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
    },
  ];

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Expose method to open sidebar from header
  useEffect(() => {
    (window as any).openSidebar = () => setIsMobileOpen(true);
  }, []);

  const handleMobileClose = () => {
    setIsMobileOpen(false);
  };

  // Determine if item is active based on current location
  const isItemActive = (item: NavItem) => {
    const currentPath = location.pathname;
    const lastPathSegment = currentPath.split('/').pop() || '';
    
    return currentPath === item.href || 
           currentPath.startsWith(item.href + '/') ||
           lastPathSegment === item.id ||
           (item.id === 'assigned-studies' && lastPathSegment === 'edit-study-configuration');
  };

  const handleItemClick = (href: string, itemId: string, hasSubItems: boolean, e: React.MouseEvent) => {
    // Don't navigate if it's a divider
    if (!href) {
      e.preventDefault();
      return;
    }
    
    e.preventDefault();
    navigate("/defineui" + href);
    
    if (hasSubItems) {
      setExpandedItem(expandedItem === itemId ? null : itemId);
    }

    // Close mobile sidebar on navigation
    setIsMobileOpen(false);
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`${styles.sidebarBackdrop} ${isMobileOpen ? styles.visible : ''}`}
        onClick={handleMobileClose}
      />

      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ''} ${isMobileOpen ? styles.open : ''}`}>
        {/* Sidebar Header */}
        <div className={styles.sidebarHeader}>
          <div className={styles.logoSection}>
            {!isCollapsed && (
              <div className={styles.logoText}>
                <span className={styles.brandName}>SynoSYS</span>
                <span className={styles.brandTagline}>Clinical Data Platform</span>
              </div>
            )}
          </div>
          
          {/* Desktop collapse button */}
          <button 
            className={styles.collapseButton}
            onClick={onToggle}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              {isCollapsed ? (
                <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              ) : (
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              )}
            </svg>
          </button>

          {/* Mobile close button */}
          <button 
            className={styles.mobileCloseBtn} 
            onClick={handleMobileClose}
            aria-label="Close Sidebar"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Navigation */}
        <nav className={styles.navigation}>
          <div className={styles.navSection}>
            {navigationItems.map((item) => {
              // Check if it's a divider
              if (!item.href) {
                return (
                  <div key={item.id} className={styles.navDivider}>
                    <div className={styles.dividerLine}></div>
                  </div>
                );
              }

              const isActive = isItemActive(item);
              const isExpanded = expandedItem === item.id;
              
              return (
                <div key={item.id} className={styles.navItemWrapper}>
                  <Link
                    to={item.href}
                    className={`${styles.navItem} ${isActive ? styles.active : ''}`}
                    onClick={(e) => handleItemClick(item.href, item.id, !!item.subItems, e)}
                    title={isCollapsed ? item.label : undefined}
                  >
                    <div className={styles.navItemIcon}>
                      {item.icon}
                    </div>
                    {!isCollapsed && (
                      <>
                        <span className={styles.navItemLabel}>{item.label}</span>
                      </>
                    )}
                  </Link>
                </div>
              );
            })}
          </div>
        </nav>
      </aside>
    </>
  );
};

export default IDefineSidebar;